/**
 * Types for Underwriting Approval Letter
 *
 * Used for generating PDF approval letters with data from Encompass
 */

import { parseAsLocalDate } from '@/utils/dateHelpers';

/**
 * Encompass Enhanced Condition
 * Represents a single underwriting condition from the Encompass API
 */
export interface EncompassCondition {
  id: string;
  conditionType: 'Underwriting' | 'PostClosing';
  title: string;
  internalId: string;
  internalDescription: string;
  externalId: string;
  externalDescription: string;
  externalPrintDate?: string;
  printDefinitions: ('InternalPrint' | 'ExternalPrint')[];
  application: {
    entityId: string;
    entityName: string;
    entityType: string;
    legacyId: string;
  };
  category: string; // 'App', 'Credit', 'Income', 'Property', 'Title', 'NonQM', etc.
  priorTo: 'Docs' | 'Funding' | 'Closing';
  status: 'Added' | 'Fulfilled' | 'Requested' | 'Received' | 'Reviewed' | 'Cleared' | 'Waived' | 'Rejected' | 'Expired';
  statusDate: string;
  statusOpen: boolean;
  owner: {
    entityId: string;
    entityName: string;
    entityType: string;
  };
  createdBy: {
    entityId: string;
    entityType: string;
  };
  createdDate: string;
  lastModifiedBy: {
    entityId: string;
    entityType: string;
  };
  lastModifiedDate: string;
  isRemoved: boolean;
  commentsCount: number;
  sourceOfCondition: 'AutomatedByRule' | 'Manual' | string;
}

/**
 * Lender Information
 */
export interface LenderInfo {
  name: string;
  contact: string;
  contactNmls?: string;
  contactStateLicense?: string;
  phone: string;
  fax: string;
  address: string;
  city: string;
  state: string;
  zip: string;
}

/**
 * Lender Loss Payee Information
 */
export interface LenderLossPayee {
  name: string;
  address: string;
}

/**
 * Borrower Information
 */
export interface BorrowerInfo {
  name: string;
  firstName: string;
  lastName: string;
  creditScore: number | null;
  email: string;
  phone: string;
}

/**
 * Co-Borrower Information
 */
export interface CoBorrowerInfo {
  name: string;
  firstName: string;
  lastName: string;
  creditScore: number | null;
}

/**
 * Underwriting Information
 */
export interface UnderwritingInfo {
  approvedDate: string | null;
  mustCloseBy: string | null;
  underwriter: string;
  processor: string;
  loanOfficer?: string; // From field 317
}

/**
 * Property Information
 */
export interface PropertyInfo {
  address: string;
  city: string;
  state: string;
  zip: string;
  propertyType: string;
  units: number;
  /** Encompass Field 1012: Subject Property Project Classification */
  projectClassification?: string | null;
  /** Encompass Field 3316: Is Non-Warrantable Project (derived from Y/N) */
  isNonWarrantable?: boolean;
}

/**
 * Rate Lock Information
 */
export interface LockInfo {
  noteRate: number | null;
  qualRate: number | null;
  lockDate: string | null;
  lockExpires: string | null;
  lockDays: number | null;
  investor: string | null;
}

/**
 * Loan Information
 */
export interface LoanInfo {
  totalLoanAmount: number;
  appraisedValue: number;
  salesPrice: number | null;
  ltv: number;
  cltv: number;
  lienPosition: string;
  noteRate: number | null;
  qualifyingRate: number | null;
  frontRatio: number;
  backRatio: number;
  totalIncome: number;
  proposedPiti: number;
  totalObligations: number;
  totalMonthlyPayments: number;
  loanProgram: string;
  purpose: string;
  loanType: string;
  occupancy: string;
  amortizationType: string;
  term: number;
  dueIn: number;
  docType: string;
  impounds: string;
  impoundType: string;
}

/**
 * Conditions organized by timing
 */
export interface ConditionsByType {
  ptd: EncompassCondition[]; // Prior to Docs
  ptf: EncompassCondition[]; // Prior to Funding
  ac: EncompassCondition[];  // At Closing
}

/**
 * Complete Approval Letter Data
 * Response from get-encompass-approval edge function
 */
export interface BorrowerPairInfo {
  applicationId: string;
  index: number;
  borrowerFirstName: string;
  borrowerLastName: string;
  coborrowerFirstName: string;
  coborrowerLastName: string;
  displayLabel: string;
}

export interface ApprovalLetterData {
  loanGuid: string;
  loanNumber: string;
  lender: LenderInfo;
  lenderLossPayee: LenderLossPayee;
  borrower: BorrowerInfo;
  coBorrower?: CoBorrowerInfo;
  underwriting: UnderwritingInfo;
  property: PropertyInfo;
  lock: LockInfo;
  loan: LoanInfo;
  conditions: ConditionsByType;
  borrowerPairs?: BorrowerPairInfo[];
  milestones?: any[];
  rawEntity?: any;
}

/**
 * Condition category for grouping/filtering
 */
export type ConditionCategory =
  | 'App'
  | 'Credit'
  | 'Income'
  | 'Property'
  | 'Title'
  | 'NonQM'
  | 'Gov\'t'
  | 'Closing'
  | 'Other';

/**
 * Condition timing (priorTo values)
 */
export type ConditionTiming = 'Docs' | 'Funding' | 'Closing';

/**
 * Human-readable labels for condition timing
 */
export const CONDITION_TIMING_LABELS: Record<ConditionTiming, string> = {
  Docs: 'Prior to Docs (PTD)',
  Funding: 'Prior to Funding (PTF)',
  Closing: 'At Closing (AC)'
};

/**
 * Condition status
 */
export type ConditionStatus =
  | 'Added'
  | 'Fulfilled'
  | 'Requested'
  | 'Received'
  | 'Reviewed'
  | 'Cleared'
  | 'Waived'
  | 'Rejected'
  | 'Expired';

/**
 * Status color mapping for UI
 */
export const CONDITION_STATUS_COLORS: Record<ConditionStatus, string> = {
  Added: 'bg-blue-100 text-blue-800',
  Fulfilled: 'bg-purple-100 text-purple-800',
  Requested: 'bg-yellow-100 text-yellow-800',
  Received: 'bg-indigo-100 text-indigo-800',
  Reviewed: 'bg-cyan-100 text-cyan-800',
  Cleared: 'bg-green-100 text-green-800',
  Waived: 'bg-gray-100 text-gray-800',
  Rejected: 'bg-red-100 text-red-800',
  Expired: 'bg-orange-100 text-orange-800'
};

/**
 * Format helpers
 */
export const formatCurrency = (amount: number | null | undefined): string => {
  if (amount === null || amount === undefined) {
return '$0.00';
}
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2
  }).format(amount);
};

export const formatPercent = (value: number | null | undefined, decimals = 3): string => {
  if (value === null || value === undefined) {
    return 'N/A';
  }
  return `${value.toFixed(decimals)}%`;
};

export const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) {
return '';
}
  try {
    return parseAsLocalDate(dateString).toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric'
    });
  } catch {
    return dateString;
  }
};
